import axios, { type AxiosRequestConfig } from 'axios'
import {
  useUserStore,
  useTokenStore,
  useRefreshTokenStore,
  isTokenExpired,
  toBearerToken,
} from '@/stores/user'
import appQueryClient from '@/libs/appQueryClient'

export class AuthError extends Error {
  constructor(public readonly kind: 'required' | 'unavailable' | 'stale') {
    super(`Authentication ${kind}`)
  }
}

let authVersion = 0
let verifiedToken: string | null = null
let refreshPromise: Promise<void> | null = null
let authenticationPromise: Promise<void> | null = null

export const getAuthVersion = () => authVersion

export const isAuthenticationFailure = (error: unknown) => {
  if (error instanceof AuthError) return error.kind === 'required'
  if (!axios.isAxiosError(error)) return false

  return error.response?.status === 401 || [401, 20401].includes(error.response?.data?.code)
}

const assertCurrentSession = (version: number) => {
  if (version !== authVersion) throw new AuthError('stale')
}

export const clearAuthSession = () => {
  authVersion += 1
  verifiedToken = null
  refreshPromise = null
  authenticationPromise = null
  useUserStore().clear()
  useTokenStore().clear()
  useRefreshTokenStore().clear()
  appQueryClient.clear()
}

// Auth endpoints bypass business interceptors to avoid recursive refresh/redirects.
// They share bounded waiting, response validation and explicit failure classification.
const authRequest = async (config: AxiosRequestConfig): Promise<AnyRecord> => {
  const version = authVersion

  try {
    const response = await axios.request({
      baseURL: import.meta.env.VITE_API_PREFIX,
      timeout: 15_000,
      ...config,
    })
    assertCurrentSession(version)

    if (
      response.data?.code !== 10200 ||
      !response.data.data ||
      typeof response.data.data !== 'object'
    ) {
      throw new AuthError(
        config.url === 'user/authorizeCode' ||
        [401, 20401, 20403, 40406].includes(response.data?.code)
          ? 'required'
          : 'unavailable',
      )
    }

    return response.data.data
  } catch (error) {
    assertCurrentSession(version)
    if (error instanceof AuthError) throw error
    if (isAuthenticationFailure(error)) throw new AuthError('required')
    if (
      axios.isAxiosError(error) &&
      ([20403, 40406].includes(error.response?.data?.code) ||
        (config.url === 'user/authorizeCode' && typeof error.response?.data?.code === 'number'))
    ) {
      throw new AuthError('required')
    }

    throw new AuthError('unavailable')
  }
}

export const refreshAccessToken = (): Promise<void> => {
  if (refreshPromise) return refreshPromise

  const version = authVersion
  const pending = Promise.resolve()
    .then(async () => {
      assertCurrentSession(version)
      const refreshStore = useRefreshTokenStore()
      const refreshToken = refreshStore.getRefreshToken()

      if (!refreshStore.hasValidRefreshToken()) throw new AuthError('required')

      const data = await authRequest({
        method: 'POST',
        url: 'user/refresh',
        data: { refresh_token: refreshToken },
      })
      assertCurrentSession(version)

      if (refreshStore.getRefreshToken() !== refreshToken) throw new AuthError('stale')
      const token = toBearerToken(data)

      if (!token || isTokenExpired(token, 0)) throw new AuthError('unavailable')

      useTokenStore().setToken(data)
      verifiedToken = null

      // The current backend returns only an access token; preserve the refresh token.
      if (typeof data.refresh_token === 'string' && data.refresh_token.trim()) {
        refreshStore.setRefreshToken(data)
      }
    })
    .finally(() => {
      if (refreshPromise === pending) refreshPromise = null
    })

  refreshPromise = pending
  return pending
}

export const recoverAccessToken = async (failedToken: unknown) => {
  const tokenStore = useTokenStore()
  const currentToken = tokenStore.getToken()

  // A late 401 from an older token must reuse the token already refreshed by a peer.
  if (
    tokenStore.hasValidToken() &&
    currentToken !== failedToken &&
    !isTokenExpired(currentToken, 0)
  ) {
    return
  }

  await refreshAccessToken()
}

const isValidUser = (user: AnyRecord) =>
  user &&
  (typeof user.id === 'number' || typeof user.id === 'string') &&
  typeof user.username === 'string' &&
  Boolean(user.username.trim()) &&
  user.is_active !== false &&
  user.is_active !== 0

export const ensureAuthenticated = (): Promise<void> => {
  const tokenStore = useTokenStore()

  if (
    verifiedToken === tokenStore.getToken() &&
    isValidUser(useUserStore().getUserInfo()) &&
    !isTokenExpired(verifiedToken)
  ) {
    return Promise.resolve()
  }
  if (authenticationPromise) return authenticationPromise

  const version = authVersion
  const pending = Promise.resolve()
    .then(async () => {
      assertCurrentSession(version)
      let refreshed = false

      if (!tokenStore.hasValidToken() || isTokenExpired(tokenStore.getToken())) {
        await refreshAccessToken()
        refreshed = true
      }

      assertCurrentSession(version)
      let profileToken = tokenStore.getToken()
      let user: AnyRecord

      try {
        user = await authRequest({ url: 'user/profile', headers: { Authorization: profileToken } })
      } catch (error) {
        assertCurrentSession(version)
        if (!isAuthenticationFailure(error) || refreshed) throw error

        await recoverAccessToken(profileToken)
        assertCurrentSession(version)
        profileToken = tokenStore.getToken()
        user = await authRequest({ url: 'user/profile', headers: { Authorization: profileToken } })
      }

      assertCurrentSession(version)
      if (!isValidUser(user)) throw new AuthError('required')

      useUserStore().setUserInfo(user)
      appQueryClient.setQueryData(['user', 'profile'], user)
      verifiedToken = profileToken
    })
    .finally(() => {
      if (authenticationPromise === pending) authenticationPromise = null
    })

  authenticationPromise = pending
  return pending
}

export const getAuthReturnUrl = (value: unknown): string => {
  const fallback = `${location.origin}/dashboard`
  if (typeof value !== 'string' || !value) return fallback

  try {
    const url = new URL(value, location.origin)
    const authPaths = ['/passport', '/verification-failed', '/status']

    if (url.origin !== location.origin || authPaths.includes(url.pathname.replace(/\/$/, ''))) {
      return fallback
    }

    for (const key of ['code', 'state', 'error', 'error_description']) url.searchParams.delete(key)
    return url.href
  } catch {
    return fallback
  }
}

const framework = () => {
  const redirectSSO = async (options: { url: string }) => {
    const data = await authRequest({
      url: 'user/login',
      params: { redirect_uri: getAuthReturnUrl(options.url) },
    })
    const href = typeof data.oauth_url === 'string' ? data.oauth_url : ''

    try {
      const url = new URL(href)
      if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Invalid protocol')
      return url.href
    } catch {
      throw new AuthError('unavailable')
    }
  }

  const loginByCode = async (options: { code: string; state: string }) => {
    clearAuthSession()
    const version = authVersion
    const data = await authRequest({
      method: 'POST',
      url: 'user/authorizeCode',
      data: {
        code: options.code,
        redirect_uri: encodeURIComponent(getAuthReturnUrl(options.state)),
      },
    })
    assertCurrentSession(version)
    const token = toBearerToken(data)

    if (!token || isTokenExpired(token, 0) || !isValidUser(data.user)) {
      throw new AuthError('required')
    }

    useTokenStore().setToken(data)
    useRefreshTokenStore().setRefreshToken(data)
    useUserStore().setUserInfo(data.user)
    await ensureAuthenticated()
    assertCurrentSession(version)
  }

  return { loginByCode, redirectSSO, reLogin: clearAuthSession }
}

export default framework
